import { ApiError } from '@core/http/api-error';

/**
 * Read a displayable, already-localized message off a caught error.
 *
 * Every /v2 backend call rejects with a {@link ApiError} whose `message` is
 * read FROM THE API (localized via `Accept-Language`) — see `core/http`. For
 * those we return that message verbatim. Anything else (a bug, a non-HTTP
 * throw) has no user-safe localized text, so we return `null` and let the caller
 * fall back to a generic translated string.
 */
export function errorMessageOf(err: unknown): string | null {
  return err instanceof ApiError ? err.message : null;
}

/**
 * The BACKEND's own `response.message` for this error — and ONLY that.
 *
 * Differs from {@link errorMessageOf} in the case that matters for copy quality:
 * `ApiError.message` is never empty (the HTTP layer fills it with a per-STATUS
 * default such as "an error occurred, try again"), so a caller reading it cannot
 * tell "the server explained what went wrong" from "the server said nothing".
 * This reads {@link ApiError.fromApi} and returns `null` in the second case, so
 * the caller can show a fallback that is specific to the REQUEST it made
 * ("couldn't start your free trial") instead of the same generic line everywhere.
 */
export function apiMessageOf(err: unknown): string | null {
  return err instanceof ApiError && err.fromApi ? err.message : null;
}

/**
 * The backend's machine-readable refusal code — the envelope's `data.code`
 * (`{ status: false, message, data: { code: "programme.pre_assessments_required" } }`).
 *
 * The `message` is localized prose and changes with the language and the copy team;
 * the code is the stable contract. Branch on THIS, never on the message text, and show
 * the message verbatim. `null` when the body carried no code (most errors send
 * `data: []`).
 */
export function apiCodeOf(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = (err.raw as { error?: unknown } | null)?.error;
  const data = body && typeof body === 'object' ? (body as { data?: unknown }).data : null;
  const code = data && typeof data === 'object' && !Array.isArray(data) ? (data as { code?: unknown }).code : null;
  return typeof code === 'string' && code.trim() !== '' ? code.trim() : null;
}

/**
 * The per-FIELD messages of a Laravel `422` validation failure, flattened to a
 * flat list of localized lines (`{ errors: { end_time: ["…"], date: ["…"] } }` →
 * `["…", "…"]`).
 *
 * Why this exists: on a `422` Laravel sets the envelope `message` to a generic
 * "the given data was invalid" (or, at best, only the FIRST field's message), so
 * a screen that shows `error()` alone hides everything the server actually said.
 * Surfacing the bag turns "a validation error" into "which field, and why" —
 * both for the user and for the next person debugging the payload. Duplicates
 * and blanks are dropped; anything that is not a 422-style field bag yields `[]`.
 */
export function fieldErrorsOf(err: unknown): readonly string[] {
  const bag = err instanceof ApiError ? err.fieldErrors : null;
  if (!bag || typeof bag !== 'object') return [];
  const out: string[] = [];
  for (const value of Object.values(bag)) {
    for (const line of Array.isArray(value) ? value : [value]) {
      const text = typeof line === 'string' ? line.trim() : '';
      if (text && !out.includes(text)) out.push(text);
    }
  }
  return out;
}
